import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Crown, CreditCard, Shield } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Account() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const pro = user.plan === "pro";
  const [bill, setBill] = useState(null);
  useEffect(() => { api.get("/billing/me").then(({ data }) => setBill(data)).catch(() => {}); }, []);
  const portal = async () => {
    try { const { data } = await api.post("/billing/portal", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (err) { toast.error(formatErr(err)); }
  };
  const renew = bill?.plan_renews_at && ["active", "trialing", "past_due"].includes(bill.subscription_status)
    ? new Date(bill.plan_renews_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : null;
  return (
    <div className="max-w-xl fade-up">
      <p className="eyebrow mb-3">Account</p>
      <h1 className="text-4xl sm:text-5xl mb-10">Your studio</h1>
      <div className="panel divide-y divide-[#F0EAF7]">
        <Row label="Name" value={user.name} testid="account-name" />
        <Row label="Email" value={user.email} testid="account-email" />
        <div className="flex items-center justify-between p-5">
          <span className="label mb-0">Plan</span>
          <span className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] px-3 py-1 rounded-full border ${pro ? "bg-[#9C4DDC] text-white border-[#9C4DDC]" : "border-[#9C4DDC]/60 text-[#9C4DDC]"}`} data-testid="account-plan-badge">
            {pro && <Crown size={12} />}{pro ? "Pro" : "Free"}
          </span>
        </div>
        {renew && (
          <Row label={bill.cancel_at_period_end ? "Pro ends" : bill.subscription_status === "trialing" ? "Trial ends" : "Renews"} value={renew} testid="account-renewal-date" />
        )}
      </div>
      <div className="flex flex-col gap-3 mt-8">
        {bill?.stripe_customer_id && bill?.subscription_status && (
          <button className="btn-primary w-full" onClick={portal} data-testid="account-manage-billing-button"><CreditCard size={14} /> Manage billing</button>
        )}
        {!pro && <Link to="/pricing" className="btn-primary w-full" data-testid="account-upgrade-button"><Crown size={14} /> Upgrade to Pro</Link>}
        {user.is_admin && <Link to="/admin" className="btn-secondary w-full" data-testid="account-admin-link"><Shield size={14} /> Admin panel</Link>}
      </div>
      <button className="btn-secondary w-full mt-3" onClick={async () => { await logout(); nav("/login"); }} data-testid="account-logout-button">
        <LogOut size={14} /> Log out
      </button>
    </div>
  );
}

const Row = ({ label, value, testid }) => (
  <div className="flex items-center justify-between gap-4 p-5">
    <span className="label mb-0">{label}</span>
    <span className="text-sm truncate" data-testid={testid}>{value}</span>
  </div>
);
